import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { ToastService } from 'src/app/services/toast.service';
import { ListingService } from 'src/app/services/marketplace/listing.service';
import { ListingAssetService } from 'src/app/services/marketplace/listing-asset.service';
import { Listing, ListingCategory, LicenseType } from 'src/app/services/marketplace/marketplace.models';

@Component({
  selector: 'app-listing-form',
  templateUrl: './listing-form.component.html',
  styleUrls: ['./listing-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ListingFormComponent implements OnInit {
  listingId: number | null = null;
  loading = true;
  saving = false;

  categories: ListingCategory[] = [];
  licenseTypes: LicenseType[] = [];

  title = '';
  shortDescription = '';
  description = '';
  categoryId: number | null = null;
  licenseTypeId: number | null = null;
  price: number | null = null;
  aiGenerated = false;
  aiDisclosurePercent: number | null = null;
  status: 'draft' | 'published' = 'draft';

  coverImageFile: File | null = null;
  coverImagePreviewUrl: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private listingService: ListingService,
    private listingAssetService: ListingAssetService,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  get isEditing(): boolean {
    return this.listingId != null;
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.listingId = idParam ? Number(idParam) : null;

    forkJoin({
      categories: this.listingService.categories(),
      licenseTypes: this.listingService.licenseTypes(),
      listing: this.listingId ? this.listingService.get(this.listingId) : Promise.resolve(null),
    }).subscribe({
      next: ({ categories, licenseTypes, listing }) => {
        this.categories = categories;
        this.licenseTypes = licenseTypes;
        if (listing) {
          this.applyListing(listing as Listing);
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private applyListing(listing: Listing): void {
    this.title = listing.title;
    this.shortDescription = listing.short_description;
    this.description = listing.description;
    this.categoryId = listing.category;
    this.licenseTypeId = listing.license_type;
    this.price = Number(listing.price);
    this.aiGenerated = listing.ai_generated;
    this.aiDisclosurePercent = listing.ai_disclosure_percent;
    this.status = listing.status === 'published' ? 'published' : 'draft';
    this.coverImagePreviewUrl = listing.cover_image;
  }

  onCoverImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.coverImageFile = file;
    if (file) {
      this.coverImagePreviewUrl = URL.createObjectURL(file);
    }
  }

  save(): void {
    if (!this.title.trim() || this.price == null) {
      this.toaster.showNote('Add a title and a price.', 'warning');
      return;
    }

    this.saving = true;
    const payload: Partial<Listing> = {
      title: this.title,
      slug: this.slugify(this.title),
      short_description: this.shortDescription,
      description: this.description,
      category: this.categoryId,
      license_type: this.licenseTypeId,
      price: String(this.price),
      currency: 'usd',
      fulfillment_type: 'digital',
      listing_type: 'one_time',
      ai_generated: this.aiGenerated,
      ai_disclosure_percent: this.aiGenerated ? this.aiDisclosurePercent : null,
      status: this.status,
    };

    const request$ = this.isEditing
      ? this.listingService.update(this.listingId!, payload)
      : this.listingService.create(payload);

    request$.subscribe({
      next: (listing) => {
        if (this.coverImageFile) {
          this.listingAssetService.upload(listing.id, this.coverImageFile, 'image').subscribe({
            next: () => this.finishSave(),
            error: () => {
              this.toaster.showNote("Saved, but the cover picture didn't upload. Please try adding it again.", 'warning');
              this.finishSave();
            },
          });
        } else {
          this.finishSave();
        }
      },
      error: (err) => {
        this.saving = false;
        const message = err?.error?.status?.[0] || err?.error?.detail || 'Could not save this listing.';
        this.toaster.showNote(message, 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  private finishSave(): void {
    this.saving = false;
    this.toaster.showSuccess('Saved.', 'success');
    this.router.navigate(['/marketplace/vendor-dashboard']);
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      || `listing-${Date.now()}`;
  }
}
